import React, { useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/config";
import { Reveal, SplitLines } from "../components/Motion";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Check, Send } from "lucide-react";
import Seo from "../components/Seo";

const TYPES = ["Booking Question", "Payment Issue", "Complaint", "General Question"];

export default function Contact() {
  const [form, setForm] = useState({ query_type: "General Question", name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/enquiries/contact", form);
      setDone(true);
      toast.success("Query sent!");
    } catch { toast.error("Something went wrong."); }
    finally { setLoading(false); }
  };

  return (
    <div className="pt-28 md:pt-36 pb-24" data-testid="contact-page">
      <Seo
        title="Contact"
        description="Get in touch with Once Way Travel — booking questions, payment help, or a private trip idea. We reply within one working day."
        path="/contact"
      />
      <div className="mx-auto max-w-[1600px] px-5 md:px-10 grid md:grid-cols-12 gap-10 md:gap-16">
        <div className="md:col-span-5">
          <Reveal>
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-owt-muted mb-4">· Contact</div>
            <h1 className="font-display text-6xl md:text-7xl leading-[0.9] text-owt-forest">
              <SplitLines text="Say hi."/>
              <br/><span className="italic text-owt-terracotta"><SplitLines text="We reply." delay={0.15}/></span>
            </h1>
            <p className="mt-6 text-owt-muted max-w-md leading-relaxed">
              For booking queries, complaints, or an off-map idea for a private trip — the form below reaches us directly. We reply within one working day.
            </p>
          </Reveal>
        </div>

        <div className="md:col-span-7">
          {done ? (
            <div className="py-16 text-center border border-dashed border-owt-border">
              <div className="mx-auto w-16 h-16 rounded-full border-2 border-owt-terracotta flex items-center justify-center">
                <Check className="w-8 h-8 text-owt-terracotta"/>
              </div>
              <p className="mt-4 font-display text-3xl text-owt-forest">Thanks — we'll be in touch!</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5 border border-owt-border p-6 md:p-10 bg-owt-surface">
              <div>
                <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-owt-muted block mb-1">Query type</label>
                <Select value={form.query_type} onValueChange={(v)=>setForm({...form, query_type: v})}>
                  <SelectTrigger data-testid="contact-type"><SelectValue/></SelectTrigger>
                  <SelectContent>{TYPES.map((t)=><SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-owt-muted block mb-1">Name</label>
                  <Input required value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} data-testid="contact-name"/>
                </div>
                <div>
                  <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-owt-muted block mb-1">Email</label>
                  <Input required type="email" value={form.email} onChange={(e)=>setForm({...form, email: e.target.value})} data-testid="contact-email"/>
                </div>
              </div>
              <div>
                <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-owt-muted block mb-1">Phone</label>
                <Input value={form.phone} onChange={(e)=>setForm({...form, phone: e.target.value})} data-testid="contact-phone"/>
              </div>
              <div>
                <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-owt-muted block mb-1">Message</label>
                <Textarea required rows={5} value={form.message} onChange={(e)=>setForm({...form, message: e.target.value})} data-testid="contact-message"/>
              </div>
              <button disabled={loading} data-testid="contact-submit" type="submit" className="w-full bg-owt-forest text-owt-surface py-3 font-mono text-[11px] tracking-[0.25em] uppercase hover:bg-owt-terracotta transition-colors inline-flex items-center justify-center gap-2">
                {loading ? "Sending..." : (<><Send className="w-4 h-4"/> Send Message</>)}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
